/**
 * The three things a screen needs from the motion pass: whether it is moving,
 * how to be the nth row of a stagger, and a number that counts.
 *
 * The rule is `src/lib/motion.ts` and the browser plumbing is `src/motion.ts`;
 * this is the React-shaped handle on both. It is a `.ts` rather than a `.tsx`
 * because there is no markup in it — the counting hook hands back a ref and
 * writes to the node itself.
 *
 * **Why the count is a ref and not state.** A home screen with a dozen rows on
 * it is a dozen numbers counting at once; a hook that called `setState` per
 * frame would be twelve React renders per frame for text that no other part of
 * the tree cares about. Writing `textContent` on one node is what a counting
 * number actually is. React never fights it: the value it rendered is the
 * value the animation finishes on, so the two agree the moment the count is
 * over, which is the only moment React might look.
 */

import { useCallback, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import type { CSSProperties } from 'react'
import { formatMoney } from './lib/format'
import {
  COUNT_MS,
  SETTLE_MS,
  countAt,
  isSettling,
  staggerStep,
  type MotionState,
} from './lib/motion'
import { motion } from './motion-document'

/**
 * What the app is doing right now — the account's preference resolved against
 * the device, and re-read whenever either changes.
 *
 * `useSyncExternalStore` because both inputs are outside React: the session
 * arrives from a fetch, and the operating system's own setting can change
 * while somebody is looking at the screen. The snapshot is one of two strings,
 * so a component that is already right is left alone.
 */
export function useMotionState(): MotionState {
  const subscribe = useCallback((listener: () => void) => motion().subscribe(listener), [])
  const snapshot = useCallback(() => motion().state(), [])
  return useSyncExternalStore(subscribe, snapshot, () => 'full' as const)
}

export function useReducedMotion(): boolean {
  return useMotionState() === 'reduced'
}

/**
 * The nth row of a list, as a style.
 *
 * All it carries is which step of the stagger this row is on; the distance and
 * the interval are `--rise` and `--stagger` in the stylesheet, which is what
 * makes reduced motion able to flatten every list in the app by setting two
 * custom properties to zero rather than by every list remembering to ask.
 */
export function staggerStyle(index: number): CSSProperties {
  return { '--stagger-step': staggerStep(index) } as CSSProperties
}

/**
 * A number that counts to its value, and says when it has just reached zero.
 *
 * Hand it the cents, the currency and the text the element is rendered with;
 * attach the ref to whatever holds the digits. It counts from wherever it was
 * — nought on the first paint, the old balance on every one after — with
 * `easeOutExpo` over `COUNT_MS`, and it always finishes on exactly the string
 * React rendered, so the DOM and the render agree the instant it stops.
 *
 * `settling` is the celebration: true for `SETTLE_MS` after a balance
 * *arrives* at zero from either side. It drives the flash and the confetti,
 * and a balance that was already at zero never sets it — being settled up is
 * not news, becoming settled up is.
 *
 * Under reduced motion the number is simply the number: no count, no burst.
 * The flash stays, because a colour changing in place is not movement and
 * "you are square with Ben now" is worth a beat either way.
 */
export function useCountingMoney(options: {
  cents: number
  currency: string
  /** What the element reads when nothing is animating — often `—`. */
  final: string
  /** False for the numbers that are history rather than news. */
  count?: boolean
}): { ref: (node: HTMLElement | null) => void; settling: boolean } {
  const { cents, currency, final, count = true } = options
  const reduced = useReducedMotion()

  const node = useRef<HTMLElement | null>(null)
  /** The last value written to the node — where the next count starts from. */
  const shown = useRef(0)
  const [settling, setSettling] = useState(false)

  const ref = useCallback((next: HTMLElement | null) => {
    node.current = next
  }, [])

  useLayoutEffect(() => {
    const element = node.current
    if (!element) return

    const from = shown.current
    const to = cents

    // Cleared on the way out as well as on the way past: an unmount mid-flight
    // must not leave a timer holding a `setState` for a component that is gone.
    let flash: ReturnType<typeof setTimeout> | undefined
    if (isSettling(from, to)) {
      setSettling(true)
      flash = setTimeout(() => setSettling(false), SETTLE_MS)
    }

    if (!count || reduced || from === to) {
      shown.current = to
      element.textContent = final
      return () => clearTimeout(flash)
    }

    const startedAt = performance.now()
    let frame = requestAnimationFrame(function step(now: number) {
      const elapsed = now - startedAt
      const value = countAt(from, to, elapsed)
      shown.current = value
      if (elapsed >= COUNT_MS) {
        element.textContent = final
        return
      }
      element.textContent = formatMoney(value, currency)
      frame = requestAnimationFrame(step)
    })

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(flash)
    }
  }, [cents, currency, final, count, reduced])

  return { ref, settling }
}
